import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Button, Paper, CircularProgress, Divider, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import LockIcon from '@mui/icons-material/Lock';
import {
    getPublicPaymentRequest,
    initiatePaymentRequestPayment,
    getPaymentRequestPaymentStatus,
} from '../../api/api';

const BRAND_COLOR = '#004b8d';

function StatusPage({ icon, title, subtitle, color }) {
    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
            {icon}
            <Typography variant="h5" fontWeight={700} color={color}>{title}</Typography>
            <Typography variant="body1" color="textSecondary" textAlign="center">{subtitle}</Typography>
        </Box>
    );
}

export default function PaymentRequestPayPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paying, setPaying] = useState(false);
    const [paymentResult, setPaymentResult] = useState(null); // 'success' | 'failed' | null
    const pollRef = useRef(null);

    const fetchRequest = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await getPublicPaymentRequest(token);
            setData(resp);
            if (resp.status === 'COMPLETED') setPaymentResult('success');
        } catch (e) {
            if (e.status === 410) {
                setError('expired');
            } else if (e.status === 404) {
                setError('not_found');
            } else {
                setError('generic');
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchRequest(); }, [fetchRequest]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const handlePay = async () => {
        setPaying(true);
        try {
            const resp = await initiatePaymentRequestPayment(token);
            const checkoutUrl = resp.checkout_url;

            // Open MCB in popup
            const w = 600, h = 700;
            const left = (window.screen.width - w) / 2;
            const top = (window.screen.height - h) / 2;
            const paymentWindow = window.open(
                checkoutUrl,
                'paymentGateway',
                `width=${w},height=${h},top=${top},left=${left},scrollbars=yes,resizable=yes`
            );

            // Poll for status while popup is open
            let popupClosed = false;
            pollRef.current = setInterval(async () => {
                try {
                    const statusResp = await getPaymentRequestPaymentStatus(token);
                    if (statusResp.status === 'COMPLETED') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        setPaymentResult('success');
                        setPaying(false);
                        if (paymentWindow && !paymentWindow.closed) paymentWindow.close();
                        return;
                    } else if (statusResp.status === 'FAILED') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        setPaymentResult('failed');
                        setPaying(false);
                        if (paymentWindow && !paymentWindow.closed) paymentWindow.close();
                        return;
                    }
                } catch {
                    // ignore polling errors
                }

                // When popup closes, wait 10s for MCB to propagate, then poll a few more times
                if (paymentWindow && paymentWindow.closed && !popupClosed) {
                    popupClosed = true;
                    clearInterval(pollRef.current);
                    pollRef.current = null;

                    // Show processing state for 10 seconds, then poll up to 5 more times
                    await new Promise(r => setTimeout(r, 10000));

                    for (let attempt = 0; attempt < 5; attempt++) {
                        try {
                            const finalStatus = await getPaymentRequestPaymentStatus(token);
                            if (finalStatus.status === 'COMPLETED') {
                                setPaymentResult('success');
                                setPaying(false);
                                return;
                            } else if (finalStatus.status === 'FAILED') {
                                setPaymentResult('failed');
                                setPaying(false);
                                return;
                            }
                        } catch {
                            // ignore
                        }
                        await new Promise(r => setTimeout(r, 3000));
                    }
                    setPaying(false);
                }
            }, 3000);
        } catch (e) {
            const msg = e?.data?.detail || e.message || 'Failed to initiate payment';
            setError('pay_error');
            setPaymentResult(null);
            alert(msg);
            setPaying(false);
        }
    };

    const fmtAmount = (amt, cur) => `${cur} ${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Indian/Mauritius' }) : '';

    // --- RENDER ---
    const containerSx = {
        minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', flexDirection: 'column', alignItems: 'center',
        pt: { xs: 2, md: 4 }, px: 2,
    };

    if (loading) {
        return (
            <Box sx={containerSx} justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    // Error states
    if (error === 'expired') {
        return (
            <Box sx={containerSx}>
                <Header />
                <Paper sx={{ maxWidth: 520, width: '100%', p: 4, mt: 3, borderRadius: 2 }}>
                    <StatusPage
                        icon={<TimerOffIcon sx={{ fontSize: 64, color: '#757575' }} />}
                        title="Payment Link Expired"
                        subtitle="This payment link has expired and is no longer valid. Please contact AfricaPlus Direct for assistance."
                        color="textSecondary"
                    />
                </Paper>
            </Box>
        );
    }

    if (error === 'not_found') {
        return (
            <Box sx={containerSx}>
                <Header />
                <Paper sx={{ maxWidth: 520, width: '100%', p: 4, mt: 3, borderRadius: 2 }}>
                    <StatusPage
                        icon={<ErrorIcon sx={{ fontSize: 64, color: '#d32f2f' }} />}
                        title="Link Not Found"
                        subtitle="This payment link does not exist or may have been removed."
                        color="error"
                    />
                </Paper>
            </Box>
        );
    }

    // Payment completed
    if (paymentResult === 'success' || (data && data.status === 'COMPLETED')) {
        return (
            <Box sx={containerSx}>
                <Header />
                <Paper sx={{ maxWidth: 520, width: '100%', p: 4, mt: 3, borderRadius: 2 }}>
                    <StatusPage
                        icon={<CheckCircleIcon sx={{ fontSize: 64, color: '#2e7d32' }} />}
                        title="Payment Already Completed"
                        subtitle={`Your payment of ${data ? fmtAmount(data.amount, data.currency) : ''} for reference ${data?.reference || ''} has been received. Thank you!`}
                        color="#2e7d32"
                    />
                </Paper>
            </Box>
        );
    }

    // Payment failed (allow retry)
    if (paymentResult === 'failed') {
        return (
            <Box sx={containerSx}>
                <Header />
                <Paper sx={{ maxWidth: 520, width: '100%', p: 4, mt: 3, borderRadius: 2 }}>
                    <StatusPage
                        icon={<ErrorIcon sx={{ fontSize: 64, color: '#d32f2f' }} />}
                        title="Payment Failed"
                        subtitle="Your payment could not be processed. Please try again."
                        color="error"
                    />
                    <Box textAlign="center" mt={2}>
                        <Button variant="contained" size="large" onClick={() => { setPaymentResult(null); }} sx={{ bgcolor: BRAND_COLOR }}>
                            Try Again
                        </Button>
                    </Box>
                </Paper>
            </Box>
        );
    }

    // Main payment page (PENDING)
    return (
        <Box sx={containerSx}>
            <Header />
            <Paper sx={{ maxWidth: 520, width: '100%', p: { xs: 3, md: 4 }, mt: 3, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>Payment Request</Typography>

                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} my={2}>
                    <Box>
                        <Typography variant="caption" color="textSecondary">Reference</Typography>
                        <Typography fontWeight={600} sx={{ fontFamily: 'monospace' }}>{data.reference}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="textSecondary">For</Typography>
                        <Typography>{data.customer_name}</Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box mb={2}>
                    <Typography variant="caption" color="textSecondary">Purpose</Typography>
                    <Typography fontWeight={500}>{data.purpose}</Typography>
                    {data.description && <Typography variant="body2" color="textSecondary" mt={0.5}>{data.description}</Typography>}
                </Box>

                <Box bgcolor="#f0f4ff" borderRadius={2} p={2} mb={2} textAlign="center">
                    <Typography variant="caption" color="textSecondary">Amount Due</Typography>
                    <Typography variant="h4" fontWeight={700} color={BRAND_COLOR}>
                        {fmtAmount(data.amount, data.currency)}
                    </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" mb={2}>
                    Payment deadline: <strong>{fmtDate(data.expires_at)}</strong>
                </Typography>

                {data.has_documents && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Invoice documents have been sent to your email for reference.
                    </Alert>
                )}

                <Button
                    variant="contained" fullWidth size="large"
                    onClick={handlePay} disabled={paying}
                    startIcon={paying ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
                    sx={{ bgcolor: BRAND_COLOR, py: 1.5, fontSize: 16, fontWeight: 700, '&:hover': { bgcolor: '#003a6e' } }}
                >
                    {paying ? 'Processing...' : 'Pay Now'}
                </Button>

                <Typography variant="caption" display="block" textAlign="center" mt={2} color="textSecondary">
                    Payments are securely processed via Mastercard Payment Gateway
                </Typography>
            </Paper>
        </Box>
    );
}

function Header() {
    return (
        <Box textAlign="center" mb={1}>
            <img src="/APD_TopNav.png" alt="APDI" style={{ height: 48, objectFit: 'contain' }} />
        </Box>
    );
}
